/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import {TreeItem} from '@mui/x-tree-view/TreeItem';
import {useReadWriteFavorites} from "../../../../api/helper";
import ArgosFlexbox from "../../../components/Containers/ArgosFlexbox";
import {StyledListItem} from "./RecentlyViewed";
import {TRACKED_ITEMS_ICONS_MAP} from "../../../../constants/trackedItems";
import BoxWithCenteredContent from "../../../components/Containers/BoxWithCenteredContent";
import ArgosButton from "../../../components/Buttons/ArgosButton";
import ArgosDialog from "../../../components/Dialogs/ArgosDialog";
import {hasRequiredProps} from "../../../../helpers/helpers";

export default function Favorites() {
  const [favorites, setFavorites] = useReadWriteFavorites()

  /**
   * Favorites are stored in a tree structure, so we need to flatten it to display it.
   */
  const favoritesList = useMemo(() => {
    const favoritesList: any[] = []
    const traverse = (node: any) => {
      if (node?.children) {
        for (let child of node.children) {
          traverse(child)
        }
      }
      const setRequiredProps = new Set(['url', 'trackedItemUid', 'type', 'label']);
      if(hasRequiredProps(node.data, setRequiredProps)) {
        favoritesList.push(node.data)
      }
    }
    traverse(favorites)
    return favoritesList
  }, [favorites])

  const [notifyItemDeletedDialog, setNotifyItemDeletedDialog] = useState(false)
  const [notifyItemDeletedDialogData, setNotifyItemDeletedDialogData] = useState<any>(null)
  const [selectedFavorite, setSelectedFavorite] = useState<any>(null)

  return (
    <ArgosFlexbox
      rowGap={0}
      vertical
      style={{marginTop: 5, justifyContent: 'center'}}
    >
      {favoritesList?.map((item: any, i: number) => {
        const url = item?.url
        const icon = TRACKED_ITEMS_ICONS_MAP?.[item?.type] ?? null
        const markedState = item?.state // Added state to backend for artifacts that are deleted giving back ability for user to remove favorite
        let isMarked = markedState?.length > 0

        return (
          <ArgosFlexbox
            key={i}
            style={{
              justifyContent: "space-between",
              alignItems: 'center',
              borderBottom: '1px solid lightgrey',
              padding: '5px 5px 5px 0px'
            }}
          >
            <StyledListItem
              onClick={(e) => {
                if (isMarked) {
                  const message = MARKED_FAVORITE_STATES_TO_MESSAGE_MAP[markedState]
                  setNotifyItemDeletedDialogData({message})
                  setNotifyItemDeletedDialog(true)
                  setSelectedFavorite(item)
                } else {
                  window.open(url, '_blank')
                }
              }}
              data-no-dnd="true"
              style={{gap: 5, color: isMarked ? 'red' : '', width: '100%'}}
            >
              <span>{icon} </span>
              <span>{item?.label} </span>
            </StyledListItem>
          </ArgosFlexbox>
        )
      })}
      <ArgosDialog data-no-dnd="true" open={notifyItemDeletedDialog} onClose={() => setNotifyItemDeletedDialog(false)}
                   width={'auto'} height={'auto'}>
        <BoxWithCenteredContent horizontal vertical
                                style={{height: '100%', display: 'flex', flexFlow: 'column nowrap'}}>
          <div>{notifyItemDeletedDialogData?.message}</div>
          <div>
            <ArgosButton
              onClick={() => {
                setNotifyItemDeletedDialog(false)
                setFavorites({type:selectedFavorite?.type, id: selectedFavorite?.trackedItemUid, partitionKey: selectedFavorite?.partitionKey}, 'DELETE')
              }}
            >
              Confirm
            </ArgosButton>
            <ArgosButton
              onClick={() => {
                setNotifyItemDeletedDialog(false)
              }}
            >
              Cancel
            </ArgosButton>
          </div>
        </BoxWithCenteredContent>
      </ArgosDialog>
    </ArgosFlexbox>
  );
}

const MARKED_FAVORITE_STATES_TO_MESSAGE_MAP: any = {
  // Deleted favorites retrieved are marked with the flag 'DELETED' as their corresponding artifacts are deleted in the backend
  'DELETED': 'This artifact has been deleted from Argos, please click confirm to remove the favorite from your profile.',
  'PRIVATE': 'This item is now private and not accessible to you, so the favorite will be deleted.',
}